import { jsx, jsxs } from "react/jsx-runtime";
import { Columns, Column, Stack, Text } from "braid-design-system";
import omit from "lodash/omit.js";
import * as pageUrl from "@seek/adv-constants";
import { siteNames } from "@seek/melways-sites";
import { EMPLOYER_HELP_CENTRE_URL } from "@seek/shared-constants";
import { useTranslations } from "@vocab/react";
import { translations } from "../../../EmployerFooter/AuthenticatedFooterLinks/.vocab/index.mjs";
import { authLink } from "../EmployerFooter.css.mjs";
const analyticsEvents = {
  TRACK_ABOUT: "about-link-clicked",
  TRACK_HOME: "home-link-clicked",
  TRACK_LOGO: "header-logo-clicked",
  TRACK_CREATE_JOB: "create-job-link-clicked",
  TRACK_LIST_JOBS: "jobs-link-clicked",
  TRACK_LIST_JOBS_OPEN: "open-jobs-link-clicked",
  TRACK_LIST_JOBS_EXPIRED: "expired-jobs-link-clicked",
  TRACK_LIST_JOBS_DRAFT: "draft-jobs-link-clicked",
  TRACK_TALENT_SEARCH: "talent-search-link-clicked",
  TRACK_TALENT_SEARCH_MATCHES: "talent-search-candidate-matches-link-clicked",
  TRACK_TALENT_SEARCH_POOLS: "talent-search-pools-link-clicked",
  TRACK_TALENT_SEARCH_HELP: "talent-search-help-link-clicked",
  TRACK_TALENT_SEARCH_TERMS: "talent-search-terms-and-conditions-link-clicked",
  TRACK_SELECT_ACCOUNT: "select-account-link-clicked",
  TRACK_MANAGE_CANDIDATES: "job-candidates-link-clicked",
  TRACK_PRODUCTS_HUB: "products-hub-link-clicked",
  TRACK_JOB_ADS_PRODUCT: "job-ads-product-link-clicked",
  TRACK_DISCOUNTS_PRODUCT: "discounts-product-link-clicked",
  TRACK_FIND_A_RECRUITER_PRODUCT: "find-a-recruiter-link-clicked",
  TRACK_CANDIDATE_MANAGEMENT_PRODUCT: "candidate-management-product-link-clicked",
  TRACK_TALENT_SEARCH_PRODUCT: "talent-search-product-link-clicked",
  TRACK_BRANDING_PRODUCT: "branding-product-link-clicked",
  TRACK_COMPANY_REVIEWS_PRODUCT: "company-reviews-product-link-clicked",
  TRACK_COMPANY_PROFILES_PRODUCT: "company-profiles-product-link-clicked",
  TRACK_BLOG_HIRING_ADVICE: "hiring-advice-link-clicked",
  TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES: "hiring-advice-attracting-candidates-link-clicked",
  TRACK_BLOG_CATEGORY_HIRING_PROCESS: "hiring-advice-hiring-process-link-clicked",
  TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE: "hiring-advice-engaging-workforce-link-clicked",
  TRACK_BLOG_MARKET_INSIGHTS: "market-insights-link-clicked",
  TRACK_BLOG_CANDIDATE_INSIGHTS: "market-insights-candidate-insights-link-clicked",
  TRACK_BLOG_TALENT_ATTRACTION_LAB: "market-insights-talent-attraction-lab-clicked",
  TRACK_BLOG_SEEK_EMPLOYMENT_TRENDS: "market-insights-seek-employment-trends-link-clicked",
  TRACK_BLOG_SUB_SALARY_TRENDS: "market-insights-salary-trends-link-clicked",
  TRACK_BLOG_WORKFORCE_INSIGHTS: "market-insights-workforce-insights-link-clicked",
  TRACK_BLOG_REPORTS: "market-insights-reports-link-clicks",
  TRACK_ANALYTICS: "analytics-link-clicked",
  TRACK_ANALYTICS_AD_USAGE: "analytics-ad-usage-link-clicked",
  TRACK_ANALYTICS_TALENT_SEARCH_USAGE: "analytics-talent-search-usage-link-clicked",
  TRACK_ANALYTICS_ROLE_INSIGHTS: "analytics-role-insights-link-clicked",
  TRACK_SIGN_IN: "signin-link-clicked",
  TRACK_SIGN_OUT: "signout-link-clicked",
  TRACK_REGISTRATION: "registration-link-clicked",
  TRACK_ACCOUNT: "account-link-clicked",
  TRACK_ACCOUNT_DETAILS: "account-details-link-clicked",
  TRACK_YOUR_TEAM: "users-link-clicked",
  TRACK_PAYABLE_INVOICES: "payable-invoices-link-clicked",
  TRACK_INVOICE_HISTORY: "invoice-history-link-clicked",
  TRACK_COMPANY_PROFILE: "company-profile-link-clicked",
  TRACK_LOGOS: "logos-link-clicked",
  TRACK_AD_PRICE_LOOKUP: "ad-price-lookup-link-clicked",
  TRACK_SCREEN: "screen-link-clicked",
  TRACK_PRODUCTS: "products-link-clicked",
  TRACK_CONTACT_US: "contact-us-link-clicked",
  TRACK_HELP_CENTRE: "help_centre_pressed",
  TRACK_CS_PHONE: "cs-phone-link-clicked",
  TRACK_TERMS: "terms-link-clicked",
  TRACK_SECURITY_PRIVACY: "security-privacy-link-clicked",
  TRACK_INTERNATIONAL_PARTNERS: "international-partners-link-clicked",
  TRACK_PARTNER_SERVICES: "partner-services-link-clicked",
  TRACK_JOBS_SITE: "jobs-site-link-clicked",
  TRACK_BLOG_JOB_WRITING: "job-ad-writing-blog-link-clicked",
  TRACK_BLOG_TALENT_ACQUISITION: "talent-acquisition-blog-link-clicked",
  TRACK_BLOG_SCREENING_CANDIDATES: "screening-candidates-blog-link-clicked",
  TRACK_BLOG_INTERVIEWS: "interviews-blog-link-clicked",
  TRACK_BLOG_LEGAL: "legal-blog-link-clicked",
  TRACK_BLOG_EMPLOYMENT_TRENDS: "employment-trends-blog-link-clicked",
  TRACK_BLOG_EMPLOYMENT_SALARY_TRENDS: "employment-salary-trends-blog-link-clicked",
  TRACK_BLOG_LAWS_OF_ATTRACTION: "laws-of-attraction-blog-link-clicked",
  TRACK_BLOG_TALENT_TALKS: "talent-talks-blog-link-clicked",
  TRACK_BLOG_WORK_FUTURE: "future-of-work-blog-link-clicked",
  TRACK_BLOG_SALARY_TRENDS: "salary-trends-blog-link-clicked",
  // Tracking For Partner Services
  TRACK_SEEK_PASS: "seek-pass-link-clicked",
  TRACK_GRADCONNECTION: "gradconnection-link-clicked",
  TRACK_GO1: "go1-link-clicked",
  TRACK_JOBADDER: "jobadder-link-clicked",
  // Tracking For Language Selector
  trackSelectLanguage: (isMobile, language) => `${isMobile ? "mobile" : "desktop"}-select-language-${language}-clicked`
};
const baseHeaderEvent = {
  eventName: "navigation_pressed",
  actionOrigin: "global header"
};
const analyticsDesktopHeaderEvents = {
  // Logo
  brandLogo: {
    ...baseHeaderEvent,
    actionPressed: "header - brand logo"
  },
  // Home
  home: {
    ...baseHeaderEvent,
    actionPressed: "header - home"
  },
  // Jobs
  jobs: {
    ...baseHeaderEvent,
    actionPressed: "header - jobs"
  },
  jobsOpen: {
    ...baseHeaderEvent,
    actionPressed: "jobs menu - open",
    entryPoint: "jobs menu - open"
  },
  jobsExpired: {
    ...baseHeaderEvent,
    actionPressed: "jobs menu - expired",
    entryPoint: "jobs menu - expired"
  },
  jobsDraft: {
    ...baseHeaderEvent,
    actionPressed: "jobs menu - draft",
    entryPoint: "jobs menu - draft"
  },
  jobsYourCandidates: {
    ...baseHeaderEvent,
    actionPressed: "jobs menu - your candidates"
  },
  // Talent Search
  talentSearch: {
    ...baseHeaderEvent,
    actionPressed: "header - talent search"
  },
  talentSearchRoot: {
    ...baseHeaderEvent,
    actionPressed: "talent search menu - talent search",
    entryPoint: "talent search menu - talent search"
  },
  talentSearchMatches: {
    ...baseHeaderEvent,
    actionPressed: "talent search menu - candidate matches",
    entryPoint: "talent search menu - candidate matches"
  },
  talentSearchPools: {
    ...baseHeaderEvent,
    actionPressed: "talent search menu - pools",
    entryPoint: "talent search menu - pools"
  },
  // Products
  products: {
    ...baseHeaderEvent,
    actionPressed: "header - products",
    entryPoint: "header - products"
  },
  productsHubWhatsNew: {
    ...baseHeaderEvent,
    actionPressed: "products menu - product hub (what's new)",
    entryPoint: "products menu - product hub (what's new)"
  },
  productsJobAds: {
    ...baseHeaderEvent,
    actionPressed: "products menu - job ads",
    entryPoint: "products menu - job ads"
  },
  productsDiscounts: {
    ...baseHeaderEvent,
    actionPressed: "products menu - discounts",
    entryPoint: "products menu - discounts"
  },
  productsCandidateManagement: {
    ...baseHeaderEvent,
    actionPressed: "products menu - candidate management",
    entryPoint: "products menu - candidate management"
  },
  productsTalentSearch: {
    ...baseHeaderEvent,
    actionPressed: "products menu - talent search",
    entryPoint: "products menu - talent search"
  },
  productsBranding: {
    ...baseHeaderEvent,
    actionPressed: "products menu - branding",
    entryPoint: "products menu - branding"
  },
  productsCompanyProfiles: {
    ...baseHeaderEvent,
    actionPressed: "products menu - company profiles",
    entryPoint: "products menu - company profiles"
  },
  // Hiring Advice
  hiringAdvice: {
    ...baseHeaderEvent,
    actionPressed: "header - hiring advice"
  },
  hiringAdviceAttractingCandidates: {
    ...baseHeaderEvent,
    actionPressed: "hiring advice - attracting candidates"
  },
  hiringAdviceHiringProcess: {
    ...baseHeaderEvent,
    actionPressed: "hiring advice - hiring process"
  },
  hiringAdviceEngagingYourWorkforce: {
    ...baseHeaderEvent,
    actionPressed: "hiring advice - engaging your workforce"
  },
  // Market Insights
  marketInsights: {
    ...baseHeaderEvent,
    actionPressed: "header - market insights"
  },
  marketInsightsEmploymentAndSalaryTrends: {
    ...baseHeaderEvent,
    actionPressed: "market insights - employment and salary trends"
  },
  marketInsightsCandidateInsights: {
    ...baseHeaderEvent,
    actionPressed: "market insights - candidate insights"
  },
  marketInsightsTalentAttractionLab: {
    ...baseHeaderEvent,
    actionPressed: "market insights - talent attraction lab"
  },
  marketInsightsWorkforceInsights: {
    ...baseHeaderEvent,
    actionPressed: "market insights - workforce insights"
  },
  marketInsightsReports: {
    ...baseHeaderEvent,
    actionPressed: "market insights - reports"
  },
  marketInsightsLawsOfAttraction: {
    ...baseHeaderEvent,
    actionPressed: "market insights - laws of attraction"
  },
  // Analytics
  // sub-menu defined in https://github.com/SEEK-Jobs/seekanalytics-frontend
  analytics: {
    ...baseHeaderEvent,
    actionPressed: "header - analytics"
  },
  analyticsAdUsageReport: {
    ...baseHeaderEvent,
    actionPressed: "analytics - ad usage report"
  },
  analyticsTalentSearchUsageReport: {
    ...baseHeaderEvent,
    actionPressed: "analytics - talent search usage report"
  },
  analyticsRoleInsights: {
    ...baseHeaderEvent,
    actionPressed: "analytics - role insights report"
  },
  // Unauthenticated
  signIn: {
    ...baseHeaderEvent,
    actionPressed: "header - sign in",
    entryPoint: "header - sign in"
  },
  register: {
    ...baseHeaderEvent,
    actionPressed: "header - register",
    entryPoint: "header - register"
  },
  createAJobAd: {
    ...baseHeaderEvent,
    actionPressed: "header - create a job ad",
    entryPoint: "header - create a job ad"
  }
};
const analyticsLanguageSelectorEvents = {
  toggleOpen: {
    ...baseHeaderEvent,
    actionPressed: "language toggle open"
  },
  toggleClosed: {
    ...baseHeaderEvent,
    actionPressed: "language toggle closed"
  },
  languageSelected: {
    ...baseHeaderEvent,
    entryPoint: "language toggle",
    actionPressed: "language selected"
  }
};
const analyticsAccountDropDownEvents = {
  // Toggle States
  toggleOpen: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - toggle open"
  },
  toggleClosed: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - toggle closed"
  },
  // Menu Items
  switchAccount: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - switch account",
    entryPoint: "drop down menu - switch account"
  },
  accountDetails: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - account settings"
  },
  userListYourTeam: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - user list (your team)"
  },
  payableInvoices: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - payable invoices"
  },
  invoiceHistory: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - invoice history"
  },
  companyProfile: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - company profile"
  },
  logosAndBrands: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - logos & brands"
  },
  adPriceLookup: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - ad price lookup"
  },
  contactUs: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - contact us"
  },
  signOut: {
    ...baseHeaderEvent,
    actionPressed: "drop down menu - sign out"
  }
};
const baseMobileEvent = {
  eventName: "navigation_pressed",
  actionOrigin: "mobile header"
};
const analyticsMobileMenuEvents = {
  // Main Menu
  menuToggleOpen: {
    ...baseMobileEvent,
    actionPressed: "mobile menu - toggle open"
  },
  menuToggleClosed: {
    ...baseMobileEvent,
    actionPressed: "mobile menu - toggle closed"
  },
  // Account Toggle
  accountToggle: {
    actionPressed: "header - account"
  },
  // Menu Items
  contactUs: {
    ...baseMobileEvent,
    actionPressed: "header - contactUs"
  },
  createAJobAd: {
    ...baseMobileEvent,
    actionPressed: "header - create a job ad",
    entryPoint: "header - create a job ad"
  },
  signIn: {
    ...baseMobileEvent,
    actionPressed: "header - sign in",
    entryPoint: "header - sign in"
  },
  signOut: {
    ...baseMobileEvent,
    actionPressed: "header - signOut"
  },
  register: {
    ...baseMobileEvent,
    actionPressed: "header - register",
    entryPoint: "header - register"
  }
};
const baseGlobalFooterEvent = {
  eventName: "navigation_pressed",
  actionOrigin: "global footer"
};
const analyticsGlobalFooterEvents = {
  about: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - about (seek brand)"
  },
  internationalPartners: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners"
  },
  partnerServices: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - partner services"
  },
  securityPrivacy: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - security & privacy"
  },
  termsAndConditions: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - terms & conditions"
  },
  helpCentre: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - help centre"
  },
  contactUs: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - contact us"
  },
  call: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - call"
  },
  jobsSite: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - (seek brand) jobs site"
  },
  internationalPartnersBdJobs: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - bdjobs"
  },
  internationalPartnersJobsdb: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - jobsdb"
  },
  internationalPartnersJobstreet: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - jobstreet"
  },
  internationalPartnersJoraAustralia: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - jora australia"
  },
  internationalPartnersJoraWorldwide: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - jora worldwide"
  },
  internationalPartnersSeekAustralia: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - seek australia"
  },
  internationalPartnersSeekNewZealand: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - seek new zealand"
  },
  internationalPartnersZhaopin: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - international partners - zhaopin"
  },
  partnerServicesSeekPass: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - partners services - seek pass"
  },
  partnerServicesGradConnection: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - partners services - gradconnection"
  },
  partnerServicesGo1: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - partners services - go1"
  },
  partnerServicesJobAdder: {
    ...baseGlobalFooterEvent,
    actionPressed: "footer - partners services - jobadder"
  }
};
const baseFooterListEvent = {
  eventName: "navigation_pressed",
  actionOrigin: "global footer"
};
const analyticsFooterListEvents = {
  // Jobs
  jobsOpen: {
    ...baseFooterListEvent,
    actionPressed: "jobs list - open",
    entryPoint: "jobs list - open"
  },
  jobsExpired: {
    ...baseFooterListEvent,
    actionPressed: "jobs list - expired",
    entryPoint: "jobs list - expired"
  },
  jobsDraft: {
    ...baseFooterListEvent,
    actionPressed: "jobs list - draft",
    entryPoint: "jobs list - draft"
  },
  jobsCandidates: {
    ...baseFooterListEvent,
    actionPressed: "jobs list - candidates",
    entryPoint: "jobs list - candidates"
  },
  // Talent Search
  talentSearchRoot: {
    ...baseHeaderEvent,
    actionPressed: "talent search menu - talent search",
    entryPoint: "talent search menu - talent search"
  },
  talentSearchMatches: {
    ...baseHeaderEvent,
    actionPressed: "talent search - candidate matches",
    entryPoint: "talent search - candidate matches"
  },
  talentSearchPools: {
    ...baseHeaderEvent,
    actionPressed: "talent search - pools",
    entryPoint: "talent search - pools"
  },
  talentSearchHelp: {
    ...baseHeaderEvent,
    actionPressed: "talent search - help",
    entryPoint: "talent search - help"
  },
  talentSearchTerms: {
    ...baseHeaderEvent,
    actionPressed: "talent search - terms",
    entryPoint: "talent search - terms"
  },
  // Products
  productsHubWhatsNew: {
    ...baseFooterListEvent,
    actionPressed: "products list - whats new",
    entryPoint: "products list - whats new"
  },
  productsJobAds: {
    ...baseFooterListEvent,
    actionPressed: "products list - job ads",
    entryPoint: "products list - job ads"
  },
  productsDiscounts: {
    ...baseFooterListEvent,
    actionPressed: "products list - discounts",
    entryPoint: "products list - discounts"
  },
  productsCandidateManagement: {
    ...baseFooterListEvent,
    actionPressed: "products list - candidate management",
    entryPoint: "products list - candidate management"
  },
  productsTalentSearch: {
    ...baseFooterListEvent,
    actionPressed: "products list - talent search",
    entryPoint: "products list - talent search"
  },
  productsBranding: {
    ...baseFooterListEvent,
    actionPressed: "products list - branding",
    entryPoint: "products list - branding"
  },
  productsCompanyProfiles: {
    ...baseFooterListEvent,
    actionPressed: "products list - company profiles",
    entryPoint: "products list - company profiles"
  },
  hiringAdviceAttractingCandidates: {
    ...baseFooterListEvent,
    actionPressed: "hiring advice list - attracting candidates"
  },
  hiringAdviceHiringProcess: {
    ...baseFooterListEvent,
    actionPressed: "hiring advice list - hiring process"
  },
  hiringAdviceEngagingYourWorkforce: {
    ...baseFooterListEvent,
    actionPressed: "hiring advice list - engaging your workforce"
  },
  marketInsightsLawsOfAttraction: {
    ...baseFooterListEvent,
    actionPressed: "market insights list - laws of attraction"
  },
  marketInsightsEmploymentAndSalaryTrends: {
    ...baseFooterListEvent,
    actionPressed: "market insights list -  employment and salary trends"
  },
  marketInsightsCandidateInsights: {
    ...baseFooterListEvent,
    actionPressed: "market insights list - candidate insights"
  },
  marketInsightsTalentAttractionLab: {
    ...baseFooterListEvent,
    actionPressed: "market insights list - talent attraction lab"
  },
  marketInsightsWorkforceInsights: {
    ...baseFooterListEvent,
    actionPressed: "market insights list - workforce insights"
  },
  marketInsightsReports: {
    ...baseFooterListEvent,
    actionPressed: "market insights list - reports"
  },
  // Analytics
  analyticsAdUsageReport: {
    ...baseFooterListEvent,
    actionPressed: "analytics list - ad usage report"
  },
  analyticsTalentSearchUsageReport: {
    ...baseFooterListEvent,
    actionPressed: "analytics list - talent search usage report"
  },
  // Account
  accountAccountDetails: {
    ...baseFooterListEvent,
    actionPressed: "account list - account settings"
  },
  accountLogosAndBrands: {
    ...baseFooterListEvent,
    actionPressed: "account list - logos & brands"
  },
  accountAdPriceLookup: {
    ...baseFooterListEvent,
    actionPressed: "account list - ad price lookup"
  },
  accountContactUs: {
    ...baseFooterListEvent,
    actionPressed: "account list - contact us"
  },
  accountHelpCentre: {
    ...baseFooterListEvent,
    actionPressed: "account list - help centre"
  }
};
const desktopAnlayticsEventToMobile = (desktopEvent) => {
  var _a;
  if (!desktopEvent) {
    return void 0;
  }
  return {
    ...desktopEvent,
    ...baseMobileEvent,
    actionPressed: (_a = desktopEvent == null ? void 0 : desktopEvent.actionPressed) == null ? void 0 : _a.replace(
      "drop down menu",
      "account"
    )
  };
};
const filterLinksOnSiteAndHref = (navItem, site) => {
  var _a;
  const siteIsGood = !navItem.specificSite || navItem.specificSite === site || ((_a = navItem.specificSite) == null ? void 0 : _a.includes(site));
  const hasHref = navItem.href !== null && navItem.href !== void 0;
  return siteIsGood && hasHref;
};
const getFilteredFooterNavLinks = (rawNavItems, site) => rawNavItems.map((item) => {
  if (item.subItems && item.subItems.length > 0) {
    return {
      ...item,
      subItems: item.subItems.filter(
        (subItem) => filterLinksOnSiteAndHref(subItem, site)
      )
    };
  }
  return item;
});
const useAuthenticatedLinksItems = (site, urlResolver, localisedContactInfo) => {
  const { t } = useTranslations(translations);
  const contactUsLink = (localisedContactInfo == null ? void 0 : localisedContactInfo.contactUsLink) || urlResolver({
    path: pageUrl.URL_CONTACTUS
  });
  const helpCentreLink = (localisedContactInfo == null ? void 0 : localisedContactInfo.helpCentreLink) || EMPLOYER_HELP_CENTRE_URL[site];
  const authenticatedLinks = [
    {
      id: "jobs",
      label: t("Jobs"),
      subItems: [
        {
          id: "open",
          label: t("Open"),
          href: urlResolver({ path: pageUrl.URL_LIST_JOBS_OPEN }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_OPEN,
          analyticsEvent: analyticsFooterListEvents.jobsOpen
        },
        {
          id: "expired",
          label: t("Expired"),
          href: urlResolver({ path: pageUrl.URL_LIST_JOBS_EXPIRED }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_EXPIRED,
          analyticsEvent: analyticsFooterListEvents.jobsExpired
        },
        {
          id: "draft",
          label: t("Draft"),
          href: urlResolver({ path: pageUrl.URL_LIST_JOBS_DRAFT }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_DRAFT,
          analyticsEvent: analyticsFooterListEvents.jobsDraft
        },
        {
          id: "candidates",
          label: t("Candidates"),
          href: urlResolver({ path: pageUrl.URL_YOUR_CANDIDATES }),
          "data-analytics": analyticsEvents.TRACK_MANAGE_CANDIDATES,
          analyticsEvent: analyticsFooterListEvents.jobsCandidates
        }
      ]
    },
    {
      id: "talentSearch",
      label: t("Talent search"),
      subItems: [
        {
          id: "talentSearch",
          label: t("Talent search"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH,
          analyticsEvent: analyticsFooterListEvents.talentSearchRoot
        },
        {
          id: "talentSearchCandidateMatches",
          label: t("Candidate Matches"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH_CANDIDATE_MATCHES
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_MATCHES,
          analyticsEvent: analyticsFooterListEvents.talentSearchMatches
        },
        {
          id: "talentSearchPools",
          label: t("Talent Pools"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH_POOLS
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_POOLS,
          analyticsEvent: analyticsFooterListEvents.talentSearchPools
        },
        {
          id: "talentSearchHelp",
          label: t("Support"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH_LEARN_MORE
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_HELP,
          analyticsEvent: analyticsFooterListEvents.talentSearchHelp
        },
        {
          id: "talentSearchTerms",
          label: t("Terms and conditions"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH_TERMS
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_TERMS,
          analyticsEvent: analyticsFooterListEvents.talentSearchTerms
        }
      ]
    },
    {
      id: "products",
      label: t("Products"),
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          analyticsEvent: analyticsFooterListEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          analyticsEvent: analyticsFooterListEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          analyticsEvent: analyticsFooterListEvents.productsDiscounts
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          analyticsEvent: analyticsFooterListEvents.productsCandidateManagement
        },
        {
          id: "talentSearch",
          label: t("Talent search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          analyticsEvent: analyticsFooterListEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          analyticsEvent: analyticsFooterListEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          analyticsEvent: analyticsFooterListEvents.productsCompanyProfiles
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsFooterListEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          analyticsEvent: analyticsFooterListEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsFooterListEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      subItems: [
        {
          id: "employmentAndSalaryTrends",
          label: t("Employment and salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_EMPLOYMENT_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_SALARY_TRENDS,
          analyticsEvent: analyticsFooterListEvents.marketInsightsEmploymentAndSalaryTrends
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsFooterListEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "talentAttractionLab",
          label: t("Talent Attraction Lab"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_TALENT_ATTRACTION_LAB
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_TALENT_ATTRACTION_LAB,
          analyticsEvent: analyticsFooterListEvents.marketInsightsTalentAttractionLab,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.id,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsFooterListEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "lawsOfAttraction",
          label: t("Laws of attraction"),
          href: urlResolver({
            path: pageUrl.URL_LAWS_OF_ATTRACTION,
            product: "candidate"
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_LAWS_OF_ATTRACTION,
          analyticsEvent: analyticsFooterListEvents.marketInsightsLawsOfAttraction,
          rel: "nofollow",
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          analyticsEvent: analyticsFooterListEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th,
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "analytics",
      label: t("Analytics"),
      subItems: [
        {
          id: "",
          label: t("Ad usage report"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_AD_USAGE
          }),
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_AD_USAGE,
          analyticsEvent: analyticsFooterListEvents.analyticsAdUsageReport
        },
        {
          id: "",
          label: t("Talent Search usage report"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_TALENT_SEARCH_USAGE
          }),
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_TALENT_SEARCH_USAGE,
          analyticsEvent: analyticsFooterListEvents.analyticsTalentSearchUsageReport
        }
      ]
    },
    {
      id: "account",
      label: t("Account"),
      subItems: [
        {
          id: "accountDetails",
          label: t("Account settings"),
          href: urlResolver({
            path: pageUrl.URL_ACCOUNT_DETAILS
          }),
          "data-analytics": analyticsEvents.TRACK_ACCOUNT_DETAILS,
          analyticsEvent: analyticsFooterListEvents.accountAccountDetails
        },
        {
          id: "logosAndBrands",
          label: t("Logos & Brands"),
          href: urlResolver({
            path: pageUrl.URL_ACCOUNT_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_LOGOS,
          analyticsEvent: analyticsFooterListEvents.accountLogosAndBrands
        },
        {
          id: "adPriceLookup",
          label: t("Ad price lookup"),
          href: urlResolver({
            path: pageUrl.URL_ACCOUNT_AD_PRICE_LOOKUP
          }),
          "data-analytics": analyticsEvents.TRACK_AD_PRICE_LOOKUP,
          analyticsEvent: analyticsFooterListEvents.accountAdPriceLookup
        },
        {
          id: "contactUs",
          label: t("Contact us"),
          href: contactUsLink,
          "data-analytics": analyticsEvents.TRACK_CONTACT_US,
          analyticsEvent: analyticsFooterListEvents.accountContactUs,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "helpCentre",
          label: t("Help Centre"),
          href: helpCentreLink,
          "data-analytics": analyticsEvents.TRACK_HELP_CENTRE,
          analyticsEvent: analyticsFooterListEvents.accountHelpCentre,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.th,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg
          ]
        }
      ]
    }
  ];
  return {
    authenticatedLinks: getFilteredFooterNavLinks(authenticatedLinks, site)
  };
};
const AuthenticatedFooterLinks = ({
  linkRenderer: LinkRenderer,
  site,
  permissionOptions,
  localisedContactInfo,
  urlResolver,
  onTrackedItemInteraction = () => {
  }
}) => {
  const { authenticatedLinks } = useAuthenticatedLinksItems(
    site,
    urlResolver,
    localisedContactInfo
  );
  const navItems = authenticatedLinks.filter(
    (item) => (
      // @TEMP: 15/04/25 for Talent Search only; Remove when discriminant no longer needed
      (item.id !== "talentSearch" || item.id === "talentSearch" && permissionOptions.canViewPremiumTalentSearch) && (permissionOptions.canViewManagerReports || item.id !== "analytics")
    )
  );
  return /* @__PURE__ */ jsx(Columns, { space: "small", children: navItems.map(({ label, subItems }, index) => /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Text, { size: "small", weight: "strong", children: label }),
    /* @__PURE__ */ jsx(Stack, { space: "medium", children: (subItems ? subItems : []).map(
      ({
        label: subItemLabel,
        href,
        analyticsEvent,
        ...restProps
      }, i) => /* @__PURE__ */ jsx(
        Text,
        {
          size: "small",
          weight: "medium",
          children: /* @__PURE__ */ jsx(
            LinkRenderer,
            {
              href,
              className: authLink,
              onClick: () => {
                if (analyticsEvent) {
                  onTrackedItemInteraction(analyticsEvent);
                }
              },
              ...omit(restProps, ["specificSite", "analyticsEvent"]),
              children: subItemLabel
            }
          )
        },
        `footer-nav-subitem-${i}`
      )
    ) })
  ] }) }, `footer-nav-item-${index}`)) });
};
export {
  AuthenticatedFooterLinks,
  analyticsAccountDropDownEvents,
  analyticsDesktopHeaderEvents,
  analyticsEvents,
  analyticsGlobalFooterEvents,
  analyticsLanguageSelectorEvents,
  analyticsMobileMenuEvents,
  baseMobileEvent,
  desktopAnlayticsEventToMobile,
  filterLinksOnSiteAndHref
};
